import { useDica } from '../ui/Dica.js';

/**
 * Linha de projeção. Portado de `lineChart()` no protótipo.
 *
 * Duas séries — realista e conservador — mais uma faixa de custo fixo.
 * A terceira é o que dá sentido às outras duas: a projeção só informa
 * decisão se der para ver em que mês a linha cruza o custo.
 *
 * O `viewBox` é fixo e a largura é 100%: o SVG escala sozinho e o
 * gráfico não precisa medir o contêiner nem re-renderizar no resize.
 */

export interface SerieDeLinha {
  readonly nome: string;
  readonly valores: readonly number[];
  readonly cor: string;
  /** Sem pontos e tracejada — para a faixa de custo fixo. */
  readonly referencia?: boolean;
  readonly dicaDe?: (rotulo: string, valor: number) => string;
}

const L = 900;
const A = 240;
const MARGEM = 34;

export function Linha({
  rotulos,
  series,
  minimo,
  maximo,
  formatar = (v) => v.toLocaleString('pt-BR'),
}: {
  /** O eixo x: ["Set", "Out", "Nov", "Dez"]. */
  readonly rotulos: readonly string[];
  readonly series: readonly SerieDeLinha[];
  minimo?: number;
  maximo?: number;
  formatar?: (valor: number) => string;
}) {
  const dica = useDica();

  const todos = series.flatMap((s) => [...s.valores]);
  const min = minimo ?? Math.min(...todos) * 0.9;
  const max = maximo ?? Math.max(...todos) * 1.1;

  const x = (i: number) => MARGEM + i * ((L - MARGEM * 2) / Math.max(1, rotulos.length - 1));
  const y = (v: number) => A - MARGEM - ((v - min) / (max - min || 1)) * (A - MARGEM * 2);
  const caminho = (valores: readonly number[]) =>
    valores.map((v, i) => `${i ? 'L' : 'M'} ${x(i)} ${y(v)}`).join(' ');

  return (
    <svg
      viewBox={`0 0 ${L} ${A}`}
      style={{ width: '100%', height: 'auto', overflow: 'visible' }}
      role="img"
      aria-label={series.map((s) => s.nome).join(' e ')}
    >
      {/* Grade horizontal: cinco linhas, discretas. */}
      {[0, 1, 2, 3, 4].map((i) => {
        const py = MARGEM + i * ((A - MARGEM * 2) / 4);
        return (
          <line
            key={i}
            x1={MARGEM}
            y1={py}
            x2={L - MARGEM}
            y2={py}
            stroke="var(--line)"
            strokeWidth={1}
          />
        );
      })}

      {series.map((s) => (
        <path
          key={s.nome}
          d={caminho(s.valores)}
          fill="none"
          stroke={s.cor}
          strokeWidth={s.referencia ? 1.5 : 2.5}
          strokeDasharray={s.referencia ? '6 6' : undefined}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}

      {series
        .filter((s) => !s.referencia)
        .map((s) =>
          s.valores.map((v, i) => (
            <circle
              key={`${s.nome}-${i}`}
              cx={x(i)}
              cy={y(v)}
              r={5}
              fill={s.cor}
              onMouseEnter={(e) =>
                dica.mostrar(e, {
                  titulo: `${rotulos[i]} · ${s.nome}`,
                  texto: s.dicaDe?.(rotulos[i] ?? '', v) ?? `<em>${formatar(v)}</em>`,
                })
              }
              onMouseMove={(e) => dica.mover(e)}
              onMouseLeave={() => dica.esconder()}
            />
          )),
        )}

      {rotulos.map((r, i) => (
        <text key={r} x={x(i)} y={A - 8} textAnchor="middle" fontSize={12} fill="var(--dim)">
          {r}
        </text>
      ))}
    </svg>
  );
}
